import throttle from 'lodash/throttle';
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { useConsumerStatusSummaryQuery } from 'apollo/queries/autogenerate/hooks';
import { useCreateKycWebApplicationMutation } from 'apollo/queries/autogenerate/hooks';
import { KycIdDocumentType } from 'apollo/queries/autogenerate/schemas';
import {
  APPLICANT_ID,
  DOCUMENT_TYPE,
  EKYC_APPLICATION_STATUS,
  EKYC_APPLICATION_STAGE,
  EKYC_SCREEN,
} from 'shared/constants/ekyc';
import { isClassicUser } from 'shared/consumer/utils/consumer-types';
import useIsWebEKYCEnabled from 'shared/modules/common/hooks/use-is-web-ekyc-enabled';
import Background from 'shared/modules/ekyc/components/atoms/Background';
import Header from 'shared/modules/ekyc/components/atoms/Header';
import Panel from 'shared/modules/ekyc/components/atoms/Panel';
import AppLoader from 'shared/modules/ekyc/components/organism/AppLoader';
import LiquidTerms from 'shared/modules/ekyc/components/organism/LiquidTerms';
import PickDocument from 'shared/modules/ekyc/components/organism/PickDocument';
import { ITEM, SCREEN } from 'shared/modules/ekyc/constants/mixpanel';
import {
  mixpanelTrackClickEvent,
  mixpanelStartTrackPageViewDurationEvent,
  mixpanelStopTrackPageViewDurationEvent,
} from 'shared/modules/ekyc/utils/kyc-tracking-events';
import { createCookie } from 'shared/utils/cookie';
import routes from 'web/navigation/routes';

const EKYCScreen = () => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(EKYC_SCREEN.PICK_DOCUMENT);
  const [submittedDocument, setSubmittedDocument] = useState<string>(
    DOCUMENT_TYPE.DRIVERS_LICENSE
  );
  const [isInitialized, setIsInitialized] = useState(false);

  const [
    createKycWebApplicationMutation,
  ] = useCreateKycWebApplicationMutation();

  const isWebKYCEnabled = useIsWebEKYCEnabled();

  const { data } = useConsumerStatusSummaryQuery();
  const kycStatus = data?.KYCApplicationStatus?.status;

  useEffect(() => {
    // Wait for isWebKYCEnabled and kycStatus to be initialized
    if (isWebKYCEnabled !== null && kycStatus) {
      // Handle redirection based on this set of conditions
      if (isClassicUser(kycStatus) && isWebKYCEnabled) {
        setIsInitialized(true);
      } else {
        return navigate(routes.authenticated.home.root);
      }
    }
  }, [isWebKYCEnabled, kycStatus]);

  const handleOnGoBack = () => {
    switch (currentScreen) {
      case EKYC_SCREEN.LIQUID_TERMS:
      default:
        setCurrentScreen(EKYC_SCREEN.PICK_DOCUMENT);
        break;
    }
  };

  const handleOnCreateApplication = throttle(
    async () => {
      try {
        mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING, {
          slide: ITEM.WEB_EKYC_ONBOARDING_SELECT_DOCUMENT,
        });

        mixpanelTrackClickEvent(SCREEN.WEB_KYC_ONBOARDING, {
          Item:
            submittedDocument === DOCUMENT_TYPE.DRIVERS_LICENSE
              ? ITEM.WEB_EKYC_ONBOARDING_PICK_ID_TYPE_DL
              : ITEM.WEB_EKYC_ONBOARDING_PICK_ID_TYPE_MNC,
        });

        const result = await createKycWebApplicationMutation({
          variables: {
            idDocumentType: submittedDocument as KycIdDocumentType,
          },
        });

        if (result.data) {
          createCookie(
            APPLICANT_ID,
            result.data.createKYCWebApplication.applicantId,
            1
          );

          createCookie(
            EKYC_APPLICATION_STATUS,
            EKYC_APPLICATION_STAGE.PRE_APPLICATION,
            1
          );

          window.location.assign(
            result.data.createKYCWebApplication.applicationUrl
          );
        }
      } catch (err: any) {
        // Handle API response if there's already a pending liquid application
        const errorCode: string = (err as any).graphQLErrors[0]?.extensions
          .code;

        if (errorCode === '409') {
          createCookie(
            EKYC_APPLICATION_STATUS,
            EKYC_APPLICATION_STAGE.PRE_APPLICATION,
            1
          );

          navigate(routes.authenticated.ekyc.apply);
        }
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  const isGoBackButtonEnabled = () => {
    if (isBigScreen) {
      return false;
    }

    if (currentScreen === EKYC_SCREEN.PICK_DOCUMENT) {
      return false;
    }

    return true;
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case EKYC_SCREEN.PICK_DOCUMENT:
        return (
          <PickDocument
            onNextButtonPress={handleOnCreateApplication}
            selectedDocument={submittedDocument as string}
            onDocumentSelect={(newDocument) =>
              setSubmittedDocument(newDocument)
            }
            onLiquidTermsButtonPress={() =>
              setCurrentScreen(EKYC_SCREEN.LIQUID_TERMS)
            }
            onFocus={() => {
              mixpanelStartTrackPageViewDurationEvent(
                SCREEN.WEB_KYC_ONBOARDING
              );
            }}
            onBlur={() =>
              mixpanelStopTrackPageViewDurationEvent(
                SCREEN.WEB_KYC_ONBOARDING,
                {
                  slide: ITEM.WEB_EKYC_ONBOARDING_SELECT_DOCUMENT,
                }
              )
            }
          />
        );
      case EKYC_SCREEN.LIQUID_TERMS:
        return (
          <LiquidTerms
            onReturnButtonPress={() =>
              setCurrentScreen(EKYC_SCREEN.PICK_DOCUMENT)
            }
            onFocus={() =>
              mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING)
            }
            onBlur={() =>
              mixpanelStopTrackPageViewDurationEvent(
                SCREEN.WEB_KYC_ONBOARDING,
                {
                  slide: ITEM.WEB_EKYC_ONBOARDING_LIQUID_T_AND_C,
                }
              )
            }
          />
        );
      default:
        return (
          <PickDocument
            onNextButtonPress={handleOnCreateApplication}
            selectedDocument={submittedDocument as string}
            onDocumentSelect={(newDocument) =>
              setSubmittedDocument(newDocument)
            }
            onLiquidTermsButtonPress={() =>
              setCurrentScreen(EKYC_SCREEN.LIQUID_TERMS)
            }
          />
        );
    }
  };

  return (
    <Background>
      {!isInitialized ? (
        <AppLoader />
      ) : (
        <View
          style={[styles.container, isBigScreen && styles.containerBigScreen]}
        >
          <Panel style={[styles.panel, isBigScreen && styles.panelBigScreen]}>
            {!isBigScreen && (
              <Header
                showBackButton={isGoBackButtonEnabled()}
                onBackButtonPress={handleOnGoBack}
              />
            )}
            {renderScreen()}
          </Panel>
        </View>
      )}
    </Background>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    minHeight: '100%',
    width: '100%',
    paddingTop: 0,
    paddingBottom: 0,
    overflow: 'hidden',
  },
  containerBigScreen: {
    flex: 1,
    position: 'relative',
    alignItems: 'center',
    paddingBottom: 30,
    paddingTop: 80,
    minHeight: 'auto',
    overflow: 'scroll',
  },
  panel: {
    maxWidth: 424,
    minHeight: '100svh',
    paddingBottom: 18,
    paddingLeft: 16,
    paddingRight: 15,
  },
  panelBigScreen: {
    paddingTop: 60,
    paddingBottom: 60,
    paddingLeft: 24,
    paddingRight: 24,
    minHeight: 'auto',
  },
});

export default EKYCScreen;
